// ============================================================================
// Terms — the /terms page. The WordPress.org plugin directory asks a plugin
// that relies on an outside service to link that service's terms of use as
// well as its privacy policy (integrations/wordpress-plugin/volgachat/readme.txt).
//
// Every product fact here matches what the code and the pricing page say
// today: the trial length (server/src/billing/trialDuration.config.ts), the
// cancel/refund answer (Pricing.tsx's FAQ), verified domains only
// (server/src/bots/verification.ts), and the plan limits
// (server/src/billing/plans.ts). When one of those changes, this page
// changes with it.
//
// Written by an engineer, not a lawyer; worth a lawyer's review when
// VolgaChat grows.
//
// The page names the service ("VolgaChat"), not the person who runs it —
// the owner's choice. If a company takes VolgaChat over, name it in the
// opening sentence, matching the business on the Dodo Payments account.

const UPDATED = '21 October 2026';
const CONTACT_EMAIL = 'hello@volgachat.com';
/** Account requests, the same support address the dashboard gives. */
const SUPPORT_EMAIL = 'support@volgachat.com';

export function Terms() {
  return (
    <section class="site-section pv-page">
      <div class="site-wrap pv-wrap">
        <span class="site-eyebrow">Terms</span>
        <h1>Terms of service</h1>
        <p class="pv-updated">Last updated {UPDATED}</p>
        <p class="site-section-lead pv-lead">
          These terms are the agreement between you and VolgaChat ("we", "us") for using VolgaChat: the website, the
          dashboard, the chat assistant and the WordPress plugin. By creating an account or using the service, you agree to them. If you
          use VolgaChat for a business, you agree on its behalf and confirm you're allowed to.
        </p>

        <div class="pv-body">
          <h2>What VolgaChat is</h2>
          <p>
            VolgaChat puts an AI chat assistant on your website. It answers your visitors from content you give it: pages of your sites,
            files you upload and answers you write. You manage your assistants, their content and their conversations in your dashboard.
          </p>

          <h2>Your account</h2>
          <ul>
            <li>You sign in with Google. Keep that account secure; you're responsible for what happens under your VolgaChat account.</li>
            <li>One person or business per account. Don't share an account to get around a plan's limits.</li>
          </ul>

          <h2>Free trial</h2>
          <p>
            New accounts can start one free trial of 14 days, with the limits shown on the pricing page. No card is needed. When the trial
            ends, your assistants stop answering visitors until you choose a paid plan. Your content and conversations stay in your account
            in the meantime, under the retention settings described in the privacy policy.
          </p>

          <h2>Paid plans and payment</h2>
          <ul>
            <li>
              Plans are billed monthly or yearly, in advance, and renew automatically until you cancel. Payments are processed by Dodo
              Payments; we never see your card details.
            </li>
            <li>
              Each plan includes a number of chatbots, pages of content and messages, as shown on the pricing page. When a limit is
              reached, the assistant stops answering or new content can't be added until the next period or an upgrade.
            </li>
            <li>
              You can cancel at any time from Manage billing on your Billing page. Your plan keeps working until the end of the period
              you've paid for, and you won't be charged again. We don't refund payments already made, including for the unused part of a
              month or year, except where the law requires it.
            </li>
            <li>
              If we change a plan's price, we'll tell you by email at least 30 days before it applies to you, and it takes effect from your
              next renewal.
            </li>
          </ul>

          <h2>Your content</h2>
          <ul>
            <li>
              Your content stays yours: your website pages, uploaded files, written answers and settings. You give us permission to store,
              process and copy it only as needed to run the service for you, including sending parts of it to our AI provider to write
              answers.
            </li>
            <li>
              You confirm you have the right to use everything you add. Only add websites you own or control: an assistant only runs on, and
              only reads, websites you have verified.
            </li>
            <li>
              Conversations on your website belong to you as the website owner. You're responsible for telling your visitors about the chat
              as your local law requires. Our <a href="/privacy">privacy policy</a> says what we keep and for how long, which you can point
              them to.
            </li>
          </ul>

          <h2>AI answers</h2>
          <p>
            Answers are written by an AI model from your content. They can be incomplete or wrong, even when your content is right. Check
            how your assistant answers, correct it with your own answers in the dashboard, and don't rely on it for medical, legal,
            financial or safety decisions. You're responsible for what your assistant tells your visitors.
          </p>

          <h2>What you can't do</h2>
          <ul>
            <li>Use VolgaChat for anything illegal, or to mislead, harass or harm people.</li>
            <li>Add content you don't have the right to use, or websites you don't control.</li>
            <li>Use an assistant to collect sensitive data such as card numbers, passwords or health records.</li>
            <li>
              Try to get around plan limits, verification or security; overload the service; or reverse-engineer it beyond what the law
              allows.
            </li>
            <li>Resell VolgaChat, or use it to build a competing service, without our written agreement.</li>
          </ul>

          <h2>The WordPress plugin</h2>
          <p>
            The VolgaChat WordPress plugin is free software under the GPL licence, version 2 or later. These terms cover the VolgaChat
            service the plugin connects to, not the plugin's code.
          </p>

          <h2>Availability and changes</h2>
          <p>
            We work to keep VolgaChat running and your data safe, but we can't promise the service will always be available or free of
            errors. We may change or remove features. If a change takes away something important you pay for, we'll tell you in advance.
          </p>

          <h2>Suspension and closing your account</h2>
          <p>
            You can stop using VolgaChat at any time. To close your account and delete its data, email{' '}
            <a href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('Close my account')}`}>{SUPPORT_EMAIL}</a>. We may suspend or close
            an account that breaks these terms, or that puts the service or other people at risk. Where we can, we'll tell you first and give
            you a chance to fix it.
          </p>

          <h2>Liability</h2>
          <p>
            VolgaChat is provided "as is". As far as the law allows, we're not liable for indirect or consequential losses, such as lost
            profits, lost sales or lost data, and our total liability to you for any claim is limited to what you paid us in the 3 months
            before it. Nothing in these terms limits liability that the law doesn't allow to be limited.
          </p>

          <h2>Changes to these terms</h2>
          <p>
            When these terms change, this page changes and the date at the top is updated. For changes that matter, we'll email account
            owners at least 30 days before they apply. Continuing to use VolgaChat after that means you accept the new terms.
          </p>

          <h2>Contact</h2>
          <p>
            Questions about these terms: <a href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent('Terms')}`}>{CONTACT_EMAIL}</a>.
          </p>
        </div>
      </div>
    </section>
  );
}
